export const EMPTY_DECISION_FORM = {
  title: '',
  description: '',
  branchId: '',
  decisionTypeId: '',
  committeeId: '',
  status: 'pending',
  priority: 'medium',
  deadline: '',
  totalMembers: 10,
  attachments: [],
};

const DECISION_SAMPLES = [
  {
    title: 'الموافقة على اعتماد الميزانية التشغيلية السنوية',
    description:
      'يعرض مجلس الإدارة للموافقة مشروع الميزانية التشغيلية للسنة المالية الحالية، بما يشمل بنود التشغيل والصيانة والرواتب والاستشارات، مع إمكانية نقل اعتمادات بين البنود وفق الأنظمة المعمول بها.',
    priority: 'high',
    totalMembers: 7,
    deadlineOffsetDays: 21,
  },
  {
    title: 'تجديد عقد توريد الخدمات المساندة',
    description:
      'اقتراح تجديد عقد التوريد مع مزود الخدمات المساندة لمدة اثني عشر شهراً ابتداءً من تاريخ انتهاء العقد الحالي، مع مراجعة أسعار الوحدة ومستوى الخدمة ومؤشرات الأداء.',
    priority: 'medium',
    totalMembers: 9,
    deadlineOffsetDays: 14,
  },
  {
    title: 'اعتماد سياسة العمل عن بُعد للإدارات الإدارية',
    description:
      'اعتماد الإطار التنظيمي للعمل الهجين والعن بُعد للإدارات غير المرتبطة بالمواقع التشغيلية، بما في ذلك أيام الحضور الإلزامية ومعايير الإنتاجية والامتثال لمتطلبات الأمن السيبراني.',
    priority: 'medium',
    totalMembers: 11,
    deadlineOffsetDays: 30,
  },
  {
    title: 'الموافقة على ترسية عطاء مشروع توسعة المستودع',
    description:
      'الموافقة على ترسية العطاء على المقاول المختار لتنفيذ أعمال المساحة الإضافية والأنظمة المساندة، وفق الجدول الزمني والقيمة المعتمدة في لجنة المناقصات.',
    priority: 'high',
    totalMembers: 7,
    deadlineOffsetDays: 18,
  },
  {
    title: 'اعتماد توصية لجنة الترشيحات وتكوين لجنة التدقيق',
    description:
      'اعتماد أسماء المرشحين لعضوية مجلس الإدارة وتشكيل لجنة التدقيق للدورة القادمة، على أن تُرفع النتائج إلى الجمعية العامة حسب نظام الشركة.',
    priority: 'low',
    totalMembers: 5,
    deadlineOffsetDays: 45,
  },
  {
    title: 'الموافقة على برنامج التوزيع النقدي للمساهمين',
    description:
      'اعتماد توزيع أرباح نقدية عن الفترة المنتهية وفق السياسة المعتمدة للشركة، مع تحديد تاريخ الاستحقاق وآلية الدفع عبر البنوك المحلية.',
    priority: 'high',
    totalMembers: 9,
    deadlineOffsetDays: 28,
  },
];

function deadlineAfterDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * @param {{ branchId: string, decisionTypeId: string }} opts – use first type for branch if you pass it from the form
 * @returns {typeof EMPTY_DECISION_FORM}
 */
export function getSampleDecisionForm(opts) {
  const n = Date.now();
  const tag = String(n).slice(-5);
  const s = DECISION_SAMPLES[n % DECISION_SAMPLES.length];
  const branchId = opts?.branchId != null && opts.branchId !== '' ? String(opts.branchId) : '';
  const decisionTypeId =
    opts?.decisionTypeId != null && opts.decisionTypeId !== ''
      ? String(opts.decisionTypeId)
      : '';
  return {
    title: `${s.title} — ${tag}`,
    description: s.description,
    branchId,
    decisionTypeId,
    status: 'pending',
    priority: s.priority,
    deadline: deadlineAfterDays(s.deadlineOffsetDays),
    totalMembers: s.totalMembers,
    attachments: [],
  };
}

/** File kind for UI; values stay stable for inferTypeFromName */
export const ATT_TYPES = [
  { value: 'pdf', labelAr: 'PDF', labelEn: 'PDF' },
  { value: 'excel', labelAr: 'Excel', labelEn: 'Excel' },
  { value: 'ppt', labelAr: 'PowerPoint', labelEn: 'PowerPoint' },
  { value: 'word', labelAr: 'Word', labelEn: 'Word' },
];

/** @param {'ar'|'en'} lang */
export function attachmentKindLabel(kind, lang = 'ar') {
  const row = ATT_TYPES.find((x) => x.value === kind);
  if (!row) return String(kind || '').toUpperCase();
  return lang === 'ar' ? row.labelAr : row.labelEn;
}

const DEFAULT_MSG_AR = {
  titleRequired: 'عنوان القرار مطلوب',
  descriptionRequired: 'وصف القرار مطلوب',
  branchRequired: 'الشركة مطلوبة',
  decisionTypeRequired: 'نوع القرار مطلوب',
  deadlineRequired: 'الموعد النهائي مطلوب',
  totalMembersRequired: 'عدد الأعضاء مطلوب',
};

/** @param {Record<string, string>} [messages] localized validation messages */
export function validateDecisionForm(form, messages = {}) {
  const m = { ...DEFAULT_MSG_AR, ...messages };
  const errors = {};
  if (!form.title.trim()) errors.title = m.titleRequired;
  if (!form.description.trim()) errors.description = m.descriptionRequired;
  if (!form.branchId) errors.branchId = m.branchRequired;
  if (!form.decisionTypeId) errors.decisionTypeId = m.decisionTypeRequired;
  if (!form.deadline) errors.deadline = m.deadlineRequired;
  if (!form.totalMembers || Number(form.totalMembers) < 1) errors.totalMembers = m.totalMembersRequired;
  return errors;
}

/** Stored/API values: pending | approved | rejected */
export function decisionStatusLabel(status, lang = 'ar') {
  const map = {
    pending: { ar: 'قيد التصويت', en: 'Pending vote' },
    approved: { ar: 'موافق عليه', en: 'Approved' },
    rejected: { ar: 'مرفوض', en: 'Rejected' },
  };
  const row = map[status];
  if (!row) return status || '';
  return lang === 'ar' ? row.ar : row.en;
}

/** Stored/API values: high | medium | low */
export function decisionPriorityLabel(priority, lang = 'ar') {
  const map = {
    high: { ar: 'عالية', en: 'High' },
    medium: { ar: 'متوسطة', en: 'Medium' },
    low: { ar: 'منخفضة', en: 'Low' },
  };
  const row = map[priority];
  if (!row) return priority || '';
  return lang === 'ar' ? row.ar : row.en;
}
